/**
 * @description Central accessor for auth tokens.
 *
 * Tokens live in sessionStorage so each tab holds its own session: an idle
 * timeout in one tab must not sign out an active tab. viewAs, realm, role and
 * the practice name travel with them, so signing out of one tab never touches
 * another tab's session.
 *
 * @author [Rajkumar Rathod]
 */

import { getTenantFromHostname } from "./get-tenant-from-hostname";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const VIEW_AS_KEY = "viewAs";
const USER_KEY = "user";
const PERMISSIONS_KEY = "permissions";
const PATIENT_KEY = "patient";
const REALM_KEY = "realm";
const ROLE_KEY = "role";
const BASE_ROLE_KEY = "baseRole";
const PROVIDER_GROUP_NAME_KEY = "pgName";
const AUTH_TYPE_KEY = "authType";
const KEYCLOAK_TOKEN_KEY = "token";
const PENDING_CATALOG_IMPORTS_KEY = "pendingCatalogImports";

export const getAccessToken = () =>
  sessionStorage.getItem(ACCESS_TOKEN_KEY) ?? "";

export const getRefreshToken = () =>
  sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? "";

export const setAccessToken = (token: string) =>
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);

export const setRefreshToken = (token: string) =>
  sessionStorage.setItem(REFRESH_TOKEN_KEY, token);

export const setAuthTokens = (accessToken: string, refreshToken: string) => {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  /** a fresh session cancels the previous one's teardown latch */
  endLogout();
};

/**
 * viewAs ("admin" | "provider" | "patient") drives both route guarding and the
 * xTENANTID header, so it must live per-tab alongside the tokens — otherwise a
 * second tab's login would repoint an active tab's tenant.
 */
export const getViewAs = () => sessionStorage.getItem(VIEW_AS_KEY);

export const setViewAs = (viewAs: string) =>
  sessionStorage.setItem(VIEW_AS_KEY, viewAs);

/** The tenant this tab authenticated against; it feeds the xTENANTID header. */
export const getStoredRealm = () =>
  sessionStorage.getItem(REALM_KEY) ?? getTenantFromHostname();

export const setStoredRealm = (realm: string) =>
  sessionStorage.setItem(REALM_KEY, realm);

export const getBaseRole = () => sessionStorage.getItem(BASE_ROLE_KEY);

export const setBaseRole = (baseRole: string) =>
  sessionStorage.setItem(BASE_ROLE_KEY, baseRole);

export const getProviderGroupName = () =>
  sessionStorage.getItem(PROVIDER_GROUP_NAME_KEY);

export const setProviderGroupName = (providerGroupName: string) =>
  sessionStorage.setItem(PROVIDER_GROUP_NAME_KEY, providerGroupName);

export const setStoredRole = (role: string) =>
  sessionStorage.setItem(ROLE_KEY, role);

export const setAuthType = (authType: string) =>
  sessionStorage.setItem(AUTH_TYPE_KEY, authType);

export const setKeycloakToken = (token: string) =>
  sessionStorage.setItem(KEYCLOAK_TOKEN_KEY, token);

/** Catalog types the tenant has not imported yet, as reported by login. */
export const getPendingCatalogImports = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(PENDING_CATALOG_IMPORTS_KEY) ?? "[]"
    );
    return Array.isArray(parsed)
      ? parsed.filter((type): type is string => typeof type === "string")
      : [];
  } catch {
    return [];
  }
};

export const setPendingCatalogImports = (types: string[]) =>
  sessionStorage.setItem(PENDING_CATALOG_IMPORTS_KEY, JSON.stringify(types));

/**
 * Clears this tab's tokens and everything scoped to its session — user,
 * privileges, tenant and the patient in context. Other tabs keep their
 * sessions.
 */
export const clearAuthTokens = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(VIEW_AS_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(PERMISSIONS_KEY);
  sessionStorage.removeItem(PATIENT_KEY);
  sessionStorage.removeItem(REALM_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  sessionStorage.removeItem(BASE_ROLE_KEY);
  sessionStorage.removeItem(PROVIDER_GROUP_NAME_KEY);
  sessionStorage.removeItem(AUTH_TYPE_KEY);
  sessionStorage.removeItem(KEYCLOAK_TOKEN_KEY);
  sessionStorage.removeItem(PENDING_CATALOG_IMPORTS_KEY);
};

/**
 * Logout keeps running after the user has already been moved to login: the
 * logout API and the FCM unregister finish in the background, and requests
 * fired before the click can still land. Any of those that 401 must not be
 * treated as an expired session — the interceptor would otherwise tear the
 * session down a second time and bounce the user to login again.
 *
 * The flag therefore stays latched until the next successful login stores a
 * token (see setAuthTokens); nothing between logout and login needs a refresh.
 */
let loggingOut = false;

export const beginLogout = () => {
  loggingOut = true;
};

export const endLogout = () => {
  loggingOut = false;
};

export const getIsLoggingOut = () => loggingOut;
